import { useEffect, useMemo, useRef, useState } from "react";
import { useExperience } from "../../../../../shared/context/ExperienceContext";
import { Icons } from "../../../../../shared/icons/icons";
import SectionHeading from "../../../../../shared/components/ui/SectionHeading/SectionHeading";
import { getModeThemeConfig } from "../../../../../shared/config/mode-theme.config";
import { getProcedureWhatsAppLink } from "../../../../../shared/config/contact.config";
import {
  proceduresData,
  proceduresSectionByMode,
} from "../../data/homeData";
import "./ProceduresSection.css";

const CATALOG_ITEMS_PER_PAGE = 4;
const SWIPE_MIN_DISTANCE = 45;
const SWIPE_MAX_VERTICAL_DISTANCE = 80;

function ProcedureIcon({ icon }) {
  if (typeof icon === "function") {
    const IconComponent = icon;
    return <IconComponent />;
  }

  const icons = {
    clipboard: Icons.ClipboardCheck,
    check: Icons.Check,
    sparkles: Icons.Sparkles,
    leaf: Icons.Leaf,
    heart: Icons.Heart,
  };

  const iconKey = String(icon || "").toLowerCase();
  const IconComponent = icons[iconKey] || Icons.ClipboardCheck;

  return <IconComponent />;
}

function getProcedureType(procedure, currentMode) {
  if (currentMode === "nutrition") return "nutrition";
  if (currentMode === "aesthetics") return "aesthetics";

  const procedureModes = Array.isArray(procedure?.modes)
    ? procedure.modes
    : procedure?.mode
      ? [procedure.mode]
      : [];

  if (procedureModes.includes("nutrition")) return "nutrition";
  if (procedureModes.includes("aesthetics")) return "aesthetics";

  const searchableText = [
    procedure?.tag,
    procedure?.eyebrow,
    procedure?.title,
    procedure?.description,
    procedure?.shortDescription,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    searchableText.includes("nutri") ||
    searchableText.includes("alimentar") ||
    searchableText.includes("emagrecimento") ||
    searchableText.includes("saúde da mulher") ||
    searchableText.includes("plano alimentar")
  ) {
    return "nutrition";
  }

  return "aesthetics";
}

function getCatalogTexts(mode) {
  if (mode === "nutrition") {
    return {
      button: "Ver todos os acompanhamentos",
      title: "Todos os acompanhamentos",
      subtitle:
        "Confira os acompanhamentos nutricionais disponíveis e escolha o cuidado que combina com seu momento.",
      eyebrow: "Nutrição",
    };
  }

  if (mode === "aesthetics") {
    return {
      button: "Ver todos os procedimentos",
      title: "Todos os procedimentos",
      subtitle:
        "Confira os procedimentos estéticos disponíveis e conheça as possibilidades de cuidado.",
      eyebrow: "Estética",
    };
  }

  return {
    button: "Ver todos os serviços",
    title: "Todos os serviços",
    subtitle:
      "Confira os acompanhamentos e procedimentos disponíveis na experiência completa.",
    eyebrow: "Experiência completa",
  };
}

export default function ProceduresSection() {
  const { mode, filterByMode } = useExperience();

  const swipeStartRef = useRef(null);
  const swipeMovedRef = useRef(false);
  const blockClickAfterSwipeRef = useRef(false);
  const activeIndexRef = useRef(0);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [catalogPage, setCatalogPage] = useState(1);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isDraggingCarousel, setIsDraggingCarousel] = useState(false);

  const sectionContent =
    proceduresSectionByMode?.[mode] || proceduresSectionByMode?.all || {};

  const theme = getModeThemeConfig(mode);
  const DecorIcon = theme.decorativeIcon || Icons.Heart;
  const FooterIcon = theme.secondaryButtonIcon || Icons.Sparkles;

  const visibleProcedures = useMemo(() => {
    if (typeof filterByMode === "function") {
      return filterByMode(proceduresData);
    }

    return proceduresData;
  }, [filterByMode]);

  const catalogTexts = getCatalogTexts(mode);
  const maxPageIndex = Math.max(0, visibleProcedures.length - 1);

  const catalogTotalPages = Math.max(
    1,
    Math.ceil(visibleProcedures.length / CATALOG_ITEMS_PER_PAGE)
  );

  const paginatedCatalogProcedures = useMemo(() => {
    const startIndex = (catalogPage - 1) * CATALOG_ITEMS_PER_PAGE;
    const endIndex = startIndex + CATALOG_ITEMS_PER_PAGE;

    return visibleProcedures.slice(startIndex, endIndex);
  }, [visibleProcedures, catalogPage]);

  const carouselCards = useMemo(() => {
    if (!visibleProcedures.length) return [];

    const previous = visibleProcedures[activeIndex - 1];
    const current = visibleProcedures[activeIndex];
    const next = visibleProcedures[activeIndex + 1];

    return [
      previous
        ? {
          procedure: previous,
          role: "secondary",
          side: "left",
          clickDirection: "left",
        }
        : null,
      current
        ? {
          procedure: current,
          role: "active",
          side: "center",
          clickDirection: null,
        }
        : null,
      next
        ? {
          procedure: next,
          role: "secondary",
          side: "right",
          clickDirection: "right",
        }
        : null,
    ].filter(Boolean);
  }, [visibleProcedures, activeIndex]);

  const scrollCarousel = (direction) => {
    setActiveIndex((current) => {
      if (direction === "left") return Math.max(0, current - 1);
      return Math.min(maxPageIndex, current + 1);
    });
  };

  const goToProcedureIndex = (index) => {
    setActiveIndex(Math.min(Math.max(index, 0), maxPageIndex));
  };

  const handleCardClick = (direction) => {
    if (blockClickAfterSwipeRef.current) return;
    if (!direction) return;

    scrollCarousel(direction);
  };

  const handleCardKeyDown = (event, direction) => {
    if (!direction) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      scrollCarousel(direction);
    }
  };

  const handleCarouselPointerDown = (event) => {
    if (!isTouchDevice || visibleProcedures.length <= 1) return;

    swipeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };

    swipeMovedRef.current = false;
    setIsDraggingCarousel(true);
  };

  const handleCarouselPointerMove = (event) => {
    if (!isTouchDevice || !swipeStartRef.current) return;

    const deltaX = event.clientX - swipeStartRef.current.x;
    const deltaY = event.clientY - swipeStartRef.current.y;

    if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
      swipeMovedRef.current = true;
    }
  };

  const handleCarouselPointerUp = (event) => {
    if (!isTouchDevice || !swipeStartRef.current) {
      setIsDraggingCarousel(false);
      return;
    }

    const deltaX = event.clientX - swipeStartRef.current.x;
    const deltaY = event.clientY - swipeStartRef.current.y;

    const isHorizontalSwipe =
      Math.abs(deltaX) >= SWIPE_MIN_DISTANCE &&
      Math.abs(deltaY) <= SWIPE_MAX_VERTICAL_DISTANCE &&
      Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe) {
      blockClickAfterSwipeRef.current = true;

      if (deltaX < 0) {
        scrollCarousel("right");
      } else {
        scrollCarousel("left");
      }

      window.setTimeout(() => {
        blockClickAfterSwipeRef.current = false;
      }, 180);
    }

    swipeStartRef.current = null;
    swipeMovedRef.current = false;
    setIsDraggingCarousel(false);
  };

  const handleCarouselPointerCancel = () => {
    swipeStartRef.current = null;
    swipeMovedRef.current = false;
    setIsDraggingCarousel(false);
  };

  const openCatalog = () => {
    setCatalogPage(1);
    setIsCatalogOpen(true);
  };

  const closeCatalog = () => {
    setIsCatalogOpen(false);
  };

  const goToCatalogPage = (page) => {
    setCatalogPage(Math.min(Math.max(page, 1), catalogTotalPages));
  };

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    const updateInputMode = () => {
      const hasTouch =
        window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
        window.innerWidth <= 768;

      setIsTouchDevice(hasTouch);
    };

    updateInputMode();

    window.addEventListener("resize", updateInputMode);

    return () => {
      window.removeEventListener("resize", updateInputMode);
    };
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    setCatalogPage(1);
    setIsCatalogOpen(false);
    swipeStartRef.current = null;
    swipeMovedRef.current = false;
    blockClickAfterSwipeRef.current = false;
    setIsDraggingCarousel(false);
  }, [mode, visibleProcedures.length]);

  useEffect(() => {
    if (activeIndex > maxPageIndex) {
      setActiveIndex(maxPageIndex);
    }
  }, [activeIndex, maxPageIndex]);

  useEffect(() => {
    if (catalogPage > catalogTotalPages) {
      setCatalogPage(catalogTotalPages);
    }
  }, [catalogPage, catalogTotalPages]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft" && !isCatalogOpen) {
        scrollCarousel("left");
      }

      if (event.key === "ArrowRight" && !isCatalogOpen) {
        scrollCarousel("right");
      }

      if (event.key === "Escape" && isCatalogOpen) {
        closeCatalog();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [maxPageIndex, isCatalogOpen]);

  useEffect(() => {
    if (isCatalogOpen) {
      document.body.classList.add("procedures-catalog-open");
    } else {
      document.body.classList.remove("procedures-catalog-open");
    }

    return () => {
      document.body.classList.remove("procedures-catalog-open");
    };
  }, [isCatalogOpen]);

  return (
    <section className="procedures-section">
      <DecorIcon
        className="procedures-section__decor procedures-section__decor--left"
        aria-hidden="true"
      />

      <DecorIcon
        className="procedures-section__decor procedures-section__decor--right"
        aria-hidden="true"
      />

      <div className="container">
        <div className="procedures-section__heading reveal-on-scroll reveal-on-scroll--up reveal-delay-1">
          <SectionHeading
            eyebrow={sectionContent.eyebrow || "Cuidados"}
            title={
              <>
                {sectionContent.title}
                {sectionContent.titleAccent && (
                  <>
                    <br />
                    <span>{sectionContent.titleAccent}</span>
                  </>
                )}
              </>
            }
            description={
              <>
                {sectionContent.descriptionBefore ||
                  "Conheça algumas possibilidades de cuidado e"}
                {sectionContent.descriptionBreak && <br />}
                {sectionContent.descriptionAfter || " clique no que combina com"}{" "}
                <strong>
                  {sectionContent.descriptionStrong || "seu momento."}
                </strong>
              </>
            }
            className="procedures-section__heading-inner"
          />

          <DecorIcon
            className="procedures-section__heading-heart"
            aria-hidden="true"
          />
        </div>

        <div className="procedures-section__content reveal-on-scroll reveal-on-scroll--up reveal-delay-2">
          <div className="procedures-carousel-wrapper">
            <div
              className={`procedures-carousel ${isTouchDevice ? "is-swipe-enabled" : ""
                } ${isDraggingCarousel ? "is-dragging" : ""}`}
              onPointerDown={handleCarouselPointerDown}
              onPointerMove={handleCarouselPointerMove}
              onPointerUp={handleCarouselPointerUp}
              onPointerCancel={handleCarouselPointerCancel}
              onPointerLeave={handleCarouselPointerCancel}
            >
              {carouselCards.map(({ procedure, role, side, clickDirection }) => {
                const hasImage = Boolean(procedure.image);
                const isActiveCard = role === "active";
                const isSecondaryCard = role === "secondary";
                const procedureType = getProcedureType(procedure, mode);

                const whatsappHref = getProcedureWhatsAppLink({
                  title: procedure.title,
                  type: procedureType,
                  action: "schedule",
                });

                const cardClassName = [
                  "procedure-mini-card",
                  isActiveCard ? "is-active-card is-centered" : "",
                  isSecondaryCard ? "is-secondary-card" : "",
                  side === "left" ? "is-on-left" : "",
                  side === "center" ? "is-on-center" : "",
                  side === "right" ? "is-on-right" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <article
                    key={`${procedure.id}-${side}`}
                    className={cardClassName}
                    role={clickDirection ? "button" : "article"}
                    tabIndex={clickDirection ? 0 : undefined}
                    onClick={() => handleCardClick(clickDirection)}
                    onKeyDown={(event) =>
                      handleCardKeyDown(event, clickDirection)
                    }
                    aria-label={
                      isActiveCard
                        ? procedure.title
                        : side === "left"
                          ? `Voltar para ${procedure.title}`
                          : `Ir para ${procedure.title}`
                    }
                  >
                    <div
                      className={`procedure-mini-card__visual ${hasImage ? "has-image" : "has-fallback"
                        }`}
                    >
                      {hasImage && (
                        <>
                          <img
                            src={procedure.image}
                            alt={procedure.title}
                            className="procedure-mini-card__img"
                            loading="lazy"
                            draggable="false"
                          />
                          <div className="procedure-mini-card__overlay" />
                        </>
                      )}

                      <span className="procedure-mini-card__tag">
                        {procedure.tag}
                      </span>
                    </div>

                    <div className="procedure-mini-card__content">
                      <span className="procedure-mini-card__pill">
                        {procedure.tag}
                      </span>

                      <div className="procedure-mini-card__top">
                        <div className="procedure-mini-card__icon">
                          <ProcedureIcon icon={procedure.icon} />
                        </div>

                        <h3>{procedure.title}</h3>
                      </div>

                      <div className="procedure-mini-card__divider" />

                      <p>{procedure.shortDescription}</p>

                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noreferrer"
                        className="procedure-mini-card__link"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                        aria-label={`Falar sobre ${procedure.title} no WhatsApp`}
                      >
                        Fale no WhatsApp
                        <Icons.FaWhatsapp />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>

        {visibleProcedures.length > 1 && (
          <div className="procedures-carousel-navigation reveal-on-scroll reveal-on-scroll--up reveal-delay-3">
            <button
              type="button"
              className="procedures-nav-btn"
              onClick={() => scrollCarousel("left")}
              disabled={activeIndex === 0}
              aria-label="Anterior"
            >
              <Icons.ChevronRight className="procedures-nav-icon procedures-nav-icon--prev" />
            </button>

            <div className="procedures-carousel-dots">
              {visibleProcedures.map((procedure, index) => (
                <button
                  key={procedure.id}
                  type="button"
                  className={`procedures-carousel-dot ${activeIndex === index ? "is-active" : ""
                    }`}
                  onClick={() => goToProcedureIndex(index)}
                  aria-label={`Ir para ${procedure.title}`}
                />
              ))}
            </div>

            <button
              type="button"
              className="procedures-nav-btn"
              onClick={() => scrollCarousel("right")}
              disabled={activeIndex >= maxPageIndex}
              aria-label="Próximo"
            >
              <Icons.ChevronRight className="procedures-nav-icon" />
            </button>
          </div>
        )}

        <div className="procedures-section__footer reveal-on-scroll reveal-on-scroll--up reveal-delay-4">
          <button
            type="button"
            className="procedures-cta procedures-cta--primary procedures-cta--footer procedures-catalog-trigger"
            onClick={openCatalog}
          >
            <FooterIcon />
            <span>{catalogTexts.button}</span>
          </button>
        </div>
      </div>

      {isCatalogOpen && (
        <div
          className="procedures-catalog-modal"
          onClick={closeCatalog}
          aria-hidden="true"
        >
          <div
            className="procedures-catalog-modal__dialog"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={catalogTexts.title}
          >
            <button
              type="button"
              className="procedures-catalog-modal__close"
              onClick={closeCatalog}
              aria-label="Fechar modal"
            >
              <span aria-hidden="true">×</span>
            </button>

            <div className="procedures-catalog-modal__header">
              <div className="procedures-catalog-modal__eyebrow">
                <span className="procedures-catalog-modal__eyebrow-line" />

                <span className="procedures-catalog-modal__eyebrow-icon">
                  <FooterIcon />
                </span>

                <span className="procedures-catalog-modal__eyebrow-text">
                  {mode === "nutrition"
                    ? "Acompanhamentos"
                    : mode === "aesthetics"
                      ? "Procedimentos"
                      : "Serviços"}
                </span>

                <span className="procedures-catalog-modal__eyebrow-line" />
              </div>

              <h3>{catalogTexts.title}</h3>
              <p>{catalogTexts.subtitle}</p>
            </div>

            <div className="procedures-catalog-modal__body">
              <div className="procedures-catalog-modal__grid">
                {paginatedCatalogProcedures.map((procedure) => {
                  const hasImage = Boolean(procedure.image);
                  const procedureType = getProcedureType(procedure, mode);

                  const whatsappHref = getProcedureWhatsAppLink({
                    title: procedure.title,
                    type: procedureType,
                    action: "schedule",
                  });

                  return (
                    <article
                      key={procedure.id}
                      className="procedures-catalog-card"
                    >
                      <div
                        className={`procedures-catalog-card__image ${hasImage ? "has-image" : "has-fallback"
                          }`}
                      >
                        {hasImage ? (
                          <img
                            src={procedure.image}
                            alt={procedure.title}
                            loading="lazy"
                          />
                        ) : (
                          <ProcedureIcon icon={procedure.icon} />
                        )}

                        <span>{procedure.tag}</span>
                      </div>

                      <div className="procedures-catalog-card__content">
                        <div className="procedures-catalog-card__icon">
                          <ProcedureIcon icon={procedure.icon} />
                        </div>

                        <h4>{procedure.title}</h4>

                        <p>
                          {procedure.shortDescription || procedure.description}
                        </p>

                        <a
                          href={whatsappHref}
                          target="_blank"
                          rel="noreferrer"
                          className="procedures-catalog-card__whatsapp"
                          aria-label={`Falar sobre ${procedure.title} no WhatsApp`}
                        >
                          Fale no WhatsApp
                          <Icons.FaWhatsapp />
                        </a>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            {catalogTotalPages > 1 && (
              <div className="procedures-catalog-modal__footer">
                <button
                  type="button"
                  className="procedures-catalog-footer-btn"
                  onClick={() => goToCatalogPage(catalogPage - 1)}
                  disabled={catalogPage === 1}
                >
                  <Icons.ChevronRight className="procedures-catalog-footer-btn__icon procedures-catalog-footer-btn__icon--prev" />
                  <span>Anterior</span>
                </button>

                <div className="procedures-catalog-page-numbers">
                  {Array.from({ length: catalogTotalPages }).map((_, index) => {
                    const page = index + 1;

                    return (
                      <button
                        key={page}
                        type="button"
                        className={`procedures-catalog-page-number ${catalogPage === page ? "is-active" : ""
                          }`}
                        onClick={() => goToCatalogPage(page)}
                        aria-label={`Ir para página ${page}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="procedures-catalog-footer-btn"
                  onClick={() => goToCatalogPage(catalogPage + 1)}
                  disabled={catalogPage === catalogTotalPages}
                >
                  <span>Próximo</span>
                  <Icons.ChevronRight className="procedures-catalog-footer-btn__icon" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}